import { useAuth } from "@/hooks/use-auth";
import { Redirect, Link } from "wouter";
import { useGetMyTransactions } from "@workspace/api-client-react";
import { ChevronLeft, Receipt } from "lucide-react";

function fmt(ts: string) {
  return new Date(ts).toLocaleString("en-GB", {
    day: "numeric", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

const TYPE_COLOURS: Record<string, string> = {
  "Lead Unlock":   "bg-blue-100 text-blue-700",
  "Deal Listing":  "bg-amber-100 text-amber-700",
  "Event Listing": "bg-purple-100 text-purple-700",
};

export default function TransactionHistory() {
  const { user } = useAuth();
  const { data, isLoading } = useGetMyTransactions();

  if (!user) return <Redirect to="/login" />;

  const items = data?.items ?? [];

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-2xl mx-auto px-4 py-6 pb-20 space-y-5">
        <div className="flex items-center gap-3">
          <Link href="/account">
            <button className="flex items-center gap-1 text-sm text-slate-500 hover:text-primary transition-colors">
              <ChevronLeft size={16} />
              Account
            </button>
          </Link>
        </div>

        <div>
          <h1 className="text-2xl font-bold text-primary">Transaction History</h1>
          <p className="text-sm text-slate-500 mt-1">
            A record of all payments you've made on Ayrshire Hub
          </p>
        </div>

        {isLoading ? (
          <div className="text-slate-400 text-sm text-center py-10">Loading…</div>
        ) : items.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-10 text-center">
            <Receipt size={40} className="mx-auto text-slate-300 mb-3" />
            <p className="font-semibold text-slate-700">No transactions yet</p>
            <p className="text-sm text-slate-400 mt-1">
              Payments for lead unlocks, deal listings, and event listings will appear here.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((t) => (
              <div
                key={`${t.type}-${t.id}`}
                className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 space-y-2"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`inline-flex text-xs font-semibold px-2 py-0.5 rounded-full ${TYPE_COLOURS[t.type] ?? "bg-slate-100 text-slate-600"}`}>
                        {t.type}
                      </span>
                      <span className="inline-flex text-xs font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                        {t.status}
                      </span>
                    </div>
                    <p className="font-semibold text-slate-800 mt-1.5 truncate">
                      {t.description ?? "—"}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="font-bold text-lg text-slate-800">
                      {t.amount != null ? `£${t.amount.toFixed(2)}` : "—"}
                    </p>
                  </div>
                </div>

                <div className="pt-2 border-t border-slate-50 space-y-1">
                  <div className="flex justify-between text-xs text-slate-400">
                    <span>Date</span>
                    <span className="text-slate-600">{fmt(t.createdAt)}</span>
                  </div>
                  {t.transactionId && (
                    <div className="flex justify-between text-xs text-slate-400">
                      <span>Reference</span>
                      <span className="font-mono text-slate-500 truncate max-w-[200px]" title={t.transactionId}>
                        {t.transactionId}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
